import { useCallback } from "react";
import { Formik, FormikProps } from "formik";
import { Form, SubmitButton, Input, Select } from "formik-antd";
import { useNavigate } from "react-router-dom";
import Box from "/app/src/components/generic/components/box";
import { integrationService } from "/app/src/services";
import { useTranslation } from "react-i18next";
import { App, Integration } from "/app/src/models";
import { useMutation } from "@tanstack/react-query";
import { handlePromiseError } from "/app/src/helpers/api";
import { simpleSchemaBuilder } from "/app/src/helpers";
interface FormValues {
  name: string;
}

/**
 * Creates a new integration component.
 *
 * @param {Object} param0 - The component props.
 * @param {App} param0.app - The application object.
 * @param {Function} param0.setToggled - Function to toggle integration state.
 * @returns {JSX.Element} The rendered new integration component.
 */
export default function NewIntegration({
  app,
  setToggled,
}: {
  app: App;
  setToggled: () => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  /**
   * Formats form values to match the integration model.
   *
   * @param {FormValues} values - The form values.
   * @param {App} app - The application object.
   * @returns {Integration} The formatted integration object.
   */
  function formatForm(values: FormValues, app: App): Integration {
    return {
      name: values.name,
      appId: app.id,
    };
  }

  const { mutateAsync: addIntegration } = useMutation({
    mutationFn: (integration: Integration) => {
      return integrationService
        .createSingle(integration)
        .then(handlePromiseError);
    },
  });

  /**
   * Handles submission of the new integration form and navigates to the created integration.
   *
   * @param {FormValues} values - The form values submitted by the user.
   * @returns {Promise<void>} A promise that resolves when the integration is added.
   */
  const addIntegrationHandler = useCallback(
    async (values: FormValues) => {
      const formattedForm = formatForm(values, app);
      await addIntegration(formattedForm).then((response) => {
        navigate(`/apps/${app.id}/integrations/${response.integration.id}`);
        setToggled();
      });
    },
    [addIntegration, app, navigate, setToggled],
  );

  /**
   * Renders the new integration form using Formik.
   *
   * @param {FormikProps<FormValues>} props - The Formik props.
   * @returns {JSX.Element} The rendered form element.
   */
  const newIntegrationForm: (props: FormikProps<FormValues>) => JSX.Element =
    useCallback(
      ({ dirty, isSubmitting }) => (
        <Form layout="vertical">
          <div className="flex justify-between items-center">
            <Form.Item
              className="w-[425px]"
              name="name"
              hasFeedback={false}
              label={t("translation:name")}
            >
              <Input
                name="name"
                placeholder={t("translation:enter_name")}
                size="large"
              />
            </Form.Item>
            <SubmitButton
              style={{ marginTop: "6px" }}
              className="!w-[275px]"
              type="primary"
              size="large"
              block
              disabled={!dirty || isSubmitting}
            >
              {t("translation:create")} {t("translation:integration")}
            </SubmitButton>
          </div>
        </Form>
      ),
      [t],
    );
  return (
    <Box>
      <div className="newIntegration">
        <Formik
          component={newIntegrationForm}
          initialValues={{
            name: "",
          }}
          validationSchema={simpleSchemaBuilder([
            { name: "name", required: true, type: "string" },
          ])}
          onSubmit={addIntegrationHandler}
          enableReinitialize
        />
      </div>
    </Box>
  );
}
